import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useQueryClient } from 'react-query';

interface NotificationContextValue {
  optimisticUnreadCount: number;
  decrementUnreadCount: () => void;
  setUnreadCount: (count: number) => void;
  resetOptimisticCount: () => void;
  revertOptimisticDecrement: () => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [optimisticUnreadCount, setOptimisticUnreadCount] = useState(0);
  const queryClient = useQueryClient();

  const decrementUnreadCount = useCallback(() => {
    setOptimisticUnreadCount(prev => Math.max(0, prev - 1));
    
    // Also optimistically update the query cache
    queryClient.setQueryData('unreadNotificationsCount', (prevCount: number = 0) => 
      Math.max(0, prevCount - 1)
    );
  }, [queryClient]);

  const setUnreadCount = useCallback((count: number) => {
    setOptimisticUnreadCount(count);
  }, []);

  const resetOptimisticCount = useCallback(() => {
    setOptimisticUnreadCount(0);
    queryClient.setQueryData('unreadNotificationsCount', 0);
  }, [queryClient]);

  const revertOptimisticDecrement = useCallback(() => {
    setOptimisticUnreadCount(prev => prev + 1);
    
    // Also revert the query cache update
    queryClient.setQueryData('unreadNotificationsCount', (prevCount: number = 0) => 
      prevCount + 1
    );
  }, [queryClient]);

  const value = {
    optimisticUnreadCount,
    decrementUnreadCount,
    setUnreadCount,
    resetOptimisticCount,
    revertOptimisticDecrement,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};